import Seo from "@/components/Seo";
import { useCurrency } from "@/context/CurrencyContext";
import { useEffect, useState, lazy, Suspense } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import PhotoUpload from "@/components/PhotoUpload";
import { Calendar } from "@/components/ui/calendar";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useAuth } from "@/context/AuthContext";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { format } from "date-fns";
import {
  CalendarIcon,
  Clock,
  User,
  Mail,
  Phone,
  MapPin,
  CheckCircle,
  ArrowRight,
  ArrowLeft,
  RefreshCw,
  Info,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Lazy load the map component
const SaintLuciaMap = lazy(() => import("@/components/SaintLuciaMap"));

export default function BookingPage() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [searchParams] = useSearchParams();
  const preselectedService = searchParams.get("service");

  const [step, setStep] = useState(1);
  const [services, setServices] = useState([]);
  const [calloutFee, setCalloutFee] = useState(250);
  const { formatMoney } = useCurrency();
  const [serviceAreas, setServiceAreas] = useState([]); // [{island, country, is_primary, communities}]
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [bookingId, setBookingId] = useState(null);
  const [photos, setPhotos] = useState([]); // File[]
  const rebookFlag = searchParams.get("rebook") === "1";

  const [formData, setFormData] = useState({
    service_id: preselectedService || (rebookFlag && user?.last_service_id) || "",
    custom_service_description: "",
    preferred_date: null,
    preferred_time: "To be coordinated by our team",
    island: (rebookFlag && user?.last_island) || "Saint Lucia",
    community: (rebookFlag && user?.last_community) || "",
    address: (rebookFlag && user?.last_address) || user?.address || "",
    notes: "",
    is_recurring: false,
    recurring_frequency: "",
    pinned_location: (rebookFlag && user?.last_pinned_location) || null, // { lat, lng }
  });

  useEffect(() => {
    fetchServices();
    fetchCalloutFee();
    fetchServiceAreas();
  }, []);

  const fetchServiceAreas = async () => {
    try {
      const res = await axios.get(`${API}/locations`);
      setServiceAreas(res.data?.areas || []);
    } catch (e) {
      console.error("Failed to load service areas", e);
    }
  };

  // Communities for the currently selected island
  const communitiesForCurrentIsland = (() => {
    const area = serviceAreas.find(
      (a) => a.island === formData?.island
    );
    return area ? area.communities : [];
  })();

  useEffect(() => {
    if (!user) return;
    setFormData((prev) => {
      const next = { ...prev };
      // Always pre-fill the most useful last-used values when fields are empty
      if (!next.address) next.address = user.last_address || user.address || "";
      if (!next.community) next.community = user.last_community || "";
      if (!next.island || next.island === "Saint Lucia")
        next.island = user.last_island || next.island;
      if (!next.pinned_location && user.last_pinned_location)
        next.pinned_location = user.last_pinned_location;
      if (!next.service_id && rebookFlag && user.last_service_id)
        next.service_id = user.last_service_id;
      return next;
    });
  }, [user, rebookFlag]);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setServices(response.data);
    } catch (error) {
      console.error("Error fetching services:", error);
      toast.error("Failed to load services");
    } finally {
      setLoading(false);
    }
  };

  const fetchCalloutFee = async () => {
    try {
      const response = await axios.get(`${API}/callout-fee`);
      setCalloutFee(response.data.fee);
    } catch (error) {
      console.error("Error fetching callout fee:", error);
    }
  };

  const selectedService = services.find((s) => s.id === formData.service_id);
  const isOtherService = formData.service_id === "other_custom";

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const validateStep = (currentStep) => {
    if (currentStep === 1) {
      if (!formData.service_id) return false;
      if (isOtherService && !formData.custom_service_description.trim()) return false;
      return true;
    }
    if (currentStep === 2) {
      if (formData.is_recurring && !formData.recurring_frequency) {
        return false;
      }
      return formData.preferred_date;
    }
    if (currentStep === 3) {
      return formData.address;
    }
    return true;
  };

  const handleNext = () => {
    if (validateStep(step)) {
      setStep((prev) => prev + 1);
    } else {
      toast.error("Please fill in all required fields");
    }
  };

  const handleBack = () => {
    setStep((prev) => prev - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep(3)) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (!isAuthenticated) {
      toast.error("Please sign in to complete your booking");
      navigate("/login", { state: { from: { pathname: "/booking" } } });
      return;
    }

    setSubmitting(true);
    try {
      // If "Other" service, prepend custom description to notes
      let bookingNotes = formData.notes || "";
      if (isOtherService && formData.custom_service_description) {
        bookingNotes = `Custom Service Request: ${formData.custom_service_description}\n\n${bookingNotes}`.trim();
      }

      const response = await axios.post(`${API}/bookings`, {
        ...formData,
        notes: bookingNotes,
        preferred_date: format(formData.preferred_date, "yyyy-MM-dd"),
      });
      const newBookingId = response.data.id;
      setBookingId(newBookingId);

      // Upload photos (best-effort, non-blocking failures)
      if (photos.length > 0) {
        try {
          const fd = new FormData();
          photos.forEach((f) => fd.append("photos", f));
          await axios.post(`${API}/bookings/${newBookingId}/photos`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } catch (uploadErr) {
          console.error("Photo upload error:", uploadErr);
          toast.warning("Booking created, but photo upload failed. You can re-upload later.");
        }
      }

      setStep(4);
      toast.success("Booking created successfully!");
    } catch (error) {
      console.error("Error creating booking:", error);
      toast.error("Failed to create booking. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handlePayNow = () => {
    navigate(`/payment?booking_id=${bookingId}`);
  };

  return (
    <div className="min-h-screen bg-slate-50 py-12 md:py-20" data-testid="booking-page">
      <Seo
        title="Book a Handyman Online"
        description="Schedule a professional handyman in Saint Lucia and the Caribbean. Choose your service, location and date in minutes. Only XCD $250 callout fee — flat, upfront, no hidden charges."
        path="/booking"
      />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <span className="inline-block bg-orange-100 text-orange-600 text-sm font-semibold px-4 py-1.5 rounded-full mb-4">
            Book a Service
          </span>
          <h1
            className="text-3xl md:text-4xl font-bold text-slate-900 mb-4"
            style={{ fontFamily: "Manrope, sans-serif" }}
            data-testid="booking-page-title"
          >
            Schedule Your Service
          </h1>
          <p className="text-slate-600">
            One-time service or set up recurring maintenance
          </p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-12">
          {[1, 2, 3, 4].map((s, index) => (
            <div key={s} className="flex items-center">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-colors ${
                  step >= s
                    ? "bg-orange-500 text-white"
                    : "bg-slate-200 text-slate-500"
                }`}
                data-testid={`step-indicator-${s}`}
              >
                {step > s ? <CheckCircle className="w-5 h-5" /> : s}
              </div>
              {index < 3 && (
                <div
                  className={`w-16 md:w-24 h-1 mx-2 transition-colors ${
                    step > s ? "bg-orange-500" : "bg-slate-200"
                  }`}
                />
              )}
            </div>
          ))}
        </div>

        {/* Step Content */}
        <Card className="bg-white border border-slate-200 shadow-lg">
          <CardHeader>
            <CardTitle
              className="text-xl font-semibold text-slate-900"
              style={{ fontFamily: "Manrope, sans-serif" }}
            >
              {step === 1 && "Select Service"}
              {step === 2 && "Schedule & Frequency"}
              {step === 3 && "Service Details"}
              {step === 4 && "Booking Confirmed"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
              </div>
            ) : (
              <>
                {/* Step 1: Select Service */}
                {step === 1 && (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="space-y-4">
                      <Label htmlFor="service" className="text-slate-700">
                        Select a Service *
                      </Label>
                      <Select
                        value={formData.service_id}
                        onValueChange={(value) => handleInputChange("service_id", value)}
                      >
                        <SelectTrigger className="h-12" data-testid="service-select-trigger">
                          <SelectValue placeholder="Choose a service" />
                        </SelectTrigger>
                        <SelectContent className="max-h-[300px]">
                          {services.map((service) => (
                            <SelectItem key={service.id} value={service.id}>
                              {service.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {selectedService && (
                        <div className="mt-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
                          <p className="font-medium text-slate-900 mb-1">
                            {selectedService.name}
                          </p>
                          <p className="text-sm text-slate-600">{selectedService.description}</p>
                        </div>
                      )}

                      {/* Custom Service Description for "Other" */}
                      {isOtherService && (
                        <div className="mt-4">
                          <Label htmlFor="custom_service" className="text-slate-700 mb-2 block">
                            Describe the service you need *
                          </Label>
                          <Textarea
                            id="custom_service"
                            value={formData.custom_service_description}
                            onChange={(e) => handleInputChange("custom_service_description", e.target.value)}
                            placeholder="Please describe in detail what service you need..."
                            className="min-h-[100px]"
                            data-testid="custom-service-input"
                          />
                        </div>
                      )}
                      
                      {/* Callout Fee Info */}
                      <div className="mt-6 p-4 bg-orange-50 rounded-lg border border-orange-200">
                        <div className="flex items-start gap-3">
                          <Info className="w-5 h-5 text-orange-500 mt-0.5" />
                          <div>
                            <p className="font-medium text-slate-900">Only {formatMoney(calloutFee, { decimals: 0 })} Callout Fee</p>
                            <p className="text-sm text-slate-600 mt-1">
                              A flat, upfront callout fee covers your inspection visit — no hidden charges.
                              Great value to get a qualified pro to your door.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Step 2: Schedule & Frequency */}
                {step === 2 && (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3 }}
                    className="space-y-6"
                  >
                    {/* Recurring Service Toggle */}
                    <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <RefreshCw className="w-5 h-5 text-orange-500" />
                          <div>
                            <Label htmlFor="recurring" className="font-medium text-slate-900">
                              Recurring Service
                            </Label>
                            <p className="text-sm text-slate-600">Set up a regular maintenance schedule</p>
                          </div>
                        </div>
                        <Switch
                          id="recurring"
                          checked={formData.is_recurring}
                          onCheckedChange={(checked) => handleInputChange("is_recurring", checked)}
                          data-testid="recurring-switch"
                        />
                      </div>
                      
                      {formData.is_recurring && (
                        <div className="mt-4 pt-4 border-t border-slate-200">
                          <Label className="text-slate-700 mb-3 block">Frequency *</Label>
                          <RadioGroup
                            value={formData.recurring_frequency}
                            onValueChange={(value) => handleInputChange("recurring_frequency", value)}
                            className="grid grid-cols-3 gap-3"
                          >
                            <div
                              className={`p-3 border-2 rounded-lg cursor-pointer text-center transition-all ${
                                formData.recurring_frequency === "biweekly"
                                  ? "border-orange-500 bg-orange-50"
                                  : "border-slate-200"
                              }`}
                              onClick={() => handleInputChange("recurring_frequency", "biweekly")}
                            >
                              <RadioGroupItem value="biweekly" id="biweekly" className="sr-only" />
                              <Label htmlFor="biweekly" className="cursor-pointer">
                                <p className="font-medium">Bi-Weekly</p>
                                <p className="text-xs text-slate-500">Every 2 weeks</p>
                              </Label>
                            </div>
                            <div
                              className={`p-3 border-2 rounded-lg cursor-pointer text-center transition-all ${
                                formData.recurring_frequency === "monthly"
                                  ? "border-orange-500 bg-orange-50"
                                  : "border-slate-200"
                              }`}
                              onClick={() => handleInputChange("recurring_frequency", "monthly")}
                            >
                              <RadioGroupItem value="monthly" id="monthly" className="sr-only" />
                              <Label htmlFor="monthly" className="cursor-pointer">
                                <p className="font-medium">Monthly</p>
                                <p className="text-xs text-slate-500">Every month</p>
                              </Label>
                            </div>
                            <div
                              className={`p-3 border-2 rounded-lg cursor-pointer text-center transition-all ${
                                formData.recurring_frequency === "annually"
                                  ? "border-orange-500 bg-orange-50"
                                  : "border-slate-200"
                              }`}
                              onClick={() => handleInputChange("recurring_frequency", "annually")}
                            >
                              <RadioGroupItem value="annually" id="annually" className="sr-only" />
                              <Label htmlFor="annually" className="cursor-pointer">
                                <p className="font-medium">Annually</p>
                                <p className="text-xs text-slate-500">Once a year</p>
                              </Label>
                            </div>
                          </RadioGroup>
                        </div>
                      )}
                    </div>

                    <div>
                      <Label className="text-slate-700 mb-3 block">
                        {formData.is_recurring ? "First Service Date *" : "Preferred Date *"}
                      </Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className="w-full h-12 justify-start text-left font-normal"
                            data-testid="date-picker-trigger"
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {formData.preferred_date ? (
                              format(formData.preferred_date, "PPP")
                            ) : (
                              <span className="text-slate-500">Select a date</span>
                            )}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={formData.preferred_date}
                            onSelect={(date) => handleInputChange("preferred_date", date)}
                            disabled={(date) => date < new Date()}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                      <p className="text-sm text-slate-500 mt-2 flex items-start gap-2">
                        <Clock className="w-4 h-4 mt-0.5 flex-shrink-0 text-orange-500" />
                        Pick your preferred date — our management team will contact you to
                        confirm the exact appointment time.
                      </p>
                    </div>
                  </motion.div>
                )}

                {/* Step 3: Service Details */}
                {step === 3 && (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3 }}
                    className="space-y-6"
                  >
                    {/* User Info (if logged in) */}
                    {isAuthenticated && user && (
                      <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                        <div className="flex items-center gap-3">
                          <CheckCircle className="w-5 h-5 text-green-500" />
                          <div>
                            <p className="font-medium text-slate-900">Booking as {user.name}</p>
                            <p className="text-sm text-slate-600">{user.email}</p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {!isAuthenticated && (
                      <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                        <div className="flex items-center gap-3">
                          <Info className="w-5 h-5 text-yellow-600" />
                          <div>
                            <p className="font-medium text-slate-900">Sign in required</p>
                            <p className="text-sm text-slate-600">
                              Please <a href="/login" className="text-orange-500 hover:underline">sign in</a> or{" "}
                              <a href="/register" className="text-orange-500 hover:underline">create an account</a> to complete your booking.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Island Selection */}
                    <div>
                      <Label className="text-slate-700 mb-2 block">
                        Island *
                      </Label>
                      <Select
                        value={formData.island}
                        onValueChange={(value) => {
                          handleInputChange("island", value);
                          // Reset community when island changes
                          handleInputChange("community", "");
                        }}
                      >
                        <SelectTrigger className="h-12" data-testid="island-select-trigger">
                          <SelectValue placeholder="Select your island" />
                        </SelectTrigger>
                        <SelectContent>
                          {serviceAreas.length === 0 ? (
                            <SelectItem value="Saint Lucia">Saint Lucia</SelectItem>
                          ) : (
                            serviceAreas.map((a) => (
                              <SelectItem key={a.island} value={a.island}>
                                {a.island}
                                {a.is_primary ? " (Primary)" : ""}
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-slate-500 mt-1">
                        Serving {serviceAreas.length || 1} Caribbean island{serviceAreas.length > 1 ? "s" : ""} — let us know your area below.
                      </p>
                    </div>

                    {/* Community Selection */}
                    <div className="relative z-20">
                      <Label className="text-slate-700 mb-2 block">
                        Community / District *
                      </Label>
                      <Select
                        value={formData.community}
                        onValueChange={(value) => handleInputChange("community", value)}
                      >
                        <SelectTrigger className="h-12" data-testid="community-select-trigger">
                          <SelectValue placeholder="Select your community" />
                        </SelectTrigger>
                        <SelectContent className="max-h-[300px] z-50">
                          {communitiesForCurrentIsland.length > 0 ? (
                            communitiesForCurrentIsland.map((community) => (
                              <SelectItem key={community} value={community}>
                                {community}
                              </SelectItem>
                            ))
                          ) : (
                            <SelectItem value="Other">Other</SelectItem>
                          )}
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Interactive Map */}
                    <div className="relative z-0">
                      <Suspense fallback={
                        <div className="h-[250px] bg-slate-100 rounded-lg flex items-center justify-center">
                          <div className="text-slate-500 text-sm">Loading map...</div>
                        </div>
                      }>
                        <SaintLuciaMap
                          onLocationSelect={(location) => handleInputChange("pinned_location", location)}
                          initialPosition={formData.pinned_location}
                          island={formData.island}
                        />
                      </Suspense>
                    </div>

                    <div>
                      <Label htmlFor="address" className="text-slate-700 mb-2 block">
                        Street Address / Directions *
                      </Label>
                      <div className="relative">
                        <MapPin className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                        <Textarea
                          id="address"
                          value={formData.address}
                          onChange={(e) => handleInputChange("address", e.target.value)}
                          placeholder="Enter your street address or directions to your property"
                          className="pl-10 min-h-[80px]"
                          data-testid="customer-address-input"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="notes" className="text-slate-700 mb-2 block">
                        Additional Notes (Optional)
                      </Label>
                      <Textarea
                        id="notes"
                        value={formData.notes}
                        onChange={(e) => handleInputChange("notes", e.target.value)}
                        placeholder="Any special instructions or details about the service needed"
                        className="min-h-[100px]"
                        data-testid="customer-notes-input"
                      />
                    </div>

                    {/* Photo upload */}
                    <PhotoUpload
                      value={photos}
                      onChange={setPhotos}
                      label="Share photos of the issue (optional)"
                      helper="Photos help us quote faster and arrive prepared. Up to 5 · max 8MB each."
                      testId="booking-photos"
                    />

                    {/* Summary */}
                    <div className="mt-8 p-6 bg-slate-50 rounded-lg border border-slate-200">
                      <h3
                        className="font-semibold text-slate-900 mb-4"
                        style={{ fontFamily: "Manrope, sans-serif" }}
                      >
                        Booking Summary
                      </h3>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-slate-600">Service:</span>
                          <span className="font-medium text-slate-900">
                            {selectedService?.name}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-600">Date:</span>
                          <span className="font-medium text-slate-900">
                            {formData.preferred_date &&
                              format(formData.preferred_date, "PPP")}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-600">Time:</span>
                          <span className="font-medium text-slate-900">
                            {formData.preferred_time}
                          </span>
                        </div>
                        {formData.is_recurring && (
                          <div className="flex justify-between">
                            <span className="text-slate-600">Frequency:</span>
                            <span className="font-medium text-orange-500 flex items-center gap-1">
                              <RefreshCw className="w-3 h-3" />
                              {formData.recurring_frequency === "biweekly" && "Bi-Weekly"}
                              {formData.recurring_frequency === "monthly" && "Monthly"}
                              {formData.recurring_frequency === "annually" && "Annually"}
                            </span>
                          </div>
                        )}
                        <div className="flex justify-between">
                          <span className="text-slate-600">Location:</span>
                          <span className="font-medium text-slate-900">
                            {formData.community}, {formData.island}
                          </span>
                        </div>
                        <div className="border-t border-slate-200 pt-2 mt-2">
                          <div className="flex justify-between">
                            <span className="font-semibold text-slate-900">Callout Fee:</span>
                            <span className="font-bold text-xl text-orange-500">
                              {formatMoney(calloutFee)}
                            </span>
                          </div>
                          <p className="text-xs text-slate-500 mt-1">
                            Flat, upfront rate — no hidden charges
                          </p>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Step 4: Confirmation */}
                {step === 4 && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3 }}
                    className="text-center py-8"
                  >
                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <CheckCircle className="w-10 h-10 text-green-500" />
                    </div>
                    <h2
                      className="text-2xl font-bold text-slate-900 mb-2"
                      style={{ fontFamily: "Manrope, sans-serif" }}
                    >
                      Booking Confirmed!
                    </h2>
                    <p className="text-slate-600 mb-8">
                      Your booking has been created. Pay the callout fee to secure your appointment.
                    </p>
                    <div className="p-6 bg-slate-50 rounded-lg border border-slate-200 text-left mb-8">
                      <h3 className="font-semibold text-slate-900 mb-4">Booking Details</h3>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-slate-600">Booking ID:</span>
                          <span className="font-mono text-slate-900">{bookingId?.slice(0, 8)}...</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-600">Service:</span>
                          <span className="font-medium text-slate-900">
                            {selectedService?.name}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-600">Date:</span>
                          <span className="font-medium text-slate-900">
                            {formData.preferred_date && format(formData.preferred_date, "PPP")}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-600">Time:</span>
                          <span className="font-medium text-slate-900">
                            {formData.preferred_time}
                          </span>
                        </div>
                        {formData.is_recurring && (
                          <div className="flex justify-between">
                            <span className="text-slate-600">Schedule:</span>
                            <span className="font-medium text-orange-500">
                              Recurring ({formData.recurring_frequency})
                            </span>
                          </div>
                        )}
                        <div className="border-t border-slate-200 pt-2 mt-2">
                          <div className="flex justify-between">
                            <span className="font-semibold text-slate-900">Callout Fee:</span>
                            <span className="font-bold text-xl text-orange-500">
                              {formatMoney(calloutFee)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                      <Button
                        onClick={handlePayNow}
                        className="bg-orange-500 hover:bg-orange-600 text-white font-semibold h-12 px-8"
                        data-testid="pay-now-btn"
                      >
                        Pay Callout Fee
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => navigate("/dashboard")}
                        className="border-2 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white font-semibold h-12 px-8"
                        data-testid="dashboard-btn"
                      >
                        Go to Dashboard
                      </Button>
                    </div>
                  </motion.div>
                )}

                {/* Navigation Buttons */}
                {step < 4 && (
                  <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-200">
                    <Button
                      variant="outline"
                      onClick={handleBack}
                      disabled={step === 1}
                      className={step === 1 ? "invisible" : ""}
                      data-testid="back-btn"
                    >
                      <ArrowLeft className="mr-2 w-4 h-4" />
                      Back
                    </Button>
                    {step < 3 ? (
                      <Button
                        onClick={handleNext}
                        className="bg-slate-900 hover:bg-slate-800 text-white"
                        data-testid="next-btn"
                      >
                        Next
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                    ) : (
                      <Button
                        onClick={handleSubmit}
                        disabled={submitting || !isAuthenticated}
                        className="bg-orange-500 hover:bg-orange-600 text-white"
                        data-testid="submit-booking-btn"
                      >
                        {submitting ? (
                          <>
                            <span className="animate-spin mr-2">⌛</span>
                            Processing...
                          </>
                        ) : (
                          <>
                            Confirm Booking
                            <CheckCircle className="ml-2 w-4 h-4" />
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
